const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const toTime = (value: string | number | Date | null | undefined) => {
  if (value === null || value === undefined || value === "") return NaN;
  return new Date(value).getTime();
};

/** "just now", "4m ago", "3h ago", "2d ago"; older than 30 days shows the date. */
export function formatRelativeTime(
  value: string | number | Date | null | undefined,
  now: number = Date.now(),
): string {
  const time = toTime(value);
  if (Number.isNaN(time)) return "—";

  const diff = now - time;
  if (diff < 0) {
    const ahead = Math.abs(diff);
    if (ahead < MINUTE) return "in a moment";
    if (ahead < HOUR) return `in ${Math.floor(ahead / MINUTE)}m`;
    if (ahead < DAY) return `in ${Math.floor(ahead / HOUR)}h`;
    return `in ${Math.floor(ahead / DAY)}d`;
  }
  if (diff < 45 * 1000) return "just now";
  if (diff < HOUR) return `${Math.max(1, Math.floor(diff / MINUTE))}m ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
  if (diff < 30 * DAY) return `${Math.floor(diff / DAY)}d ago`;

  return new Date(time).toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatDateTime(value: string | number | Date | null | undefined) {
  const time = toTime(value);
  if (Number.isNaN(time)) return "—";
  return new Date(time).toLocaleString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** 850ms, 12.4s, 3m 20s, 1h 5m. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < MINUTE) {
    const seconds = Math.round(ms / 100) / 10;
    return `${seconds}s`;
  }
  if (ms < HOUR) {
    const minutes = Math.floor(ms / MINUTE);
    const seconds = Math.round((ms % MINUTE) / 1000);
    return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(ms / HOUR);
  const minutes = Math.round((ms % HOUR) / MINUTE);
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return value.toLocaleString();
}
